import { useOutletContext, useNavigate } from "react-router";
import { useState, useMemo, useRef, useEffect } from "react";
import { LogOut, Pencil, Check, X } from "lucide-react";
import { confirmDialog } from "./ConfirmDialog";
import { useAccountStore } from "../stores/accountStore";
import type { RawClient } from "../lib/xmpp";
import encodeQR from "qr";

type AccountOutletContext = {
	client: RawClient;
	status: string;
	persistence: any;
	accounts: Record<string, any>;
};

const statusLabel: Record<string, string> = {
	online: "Connected",
	connecting: "Connecting...",
	error: "Connection error",
	auth: "Authentication required",
};

const statusColor: Record<string, string> = {
	online: "bg-green-400",
	connecting: "bg-yellow-400",
	error: "bg-red-400",
	auth: "bg-red-400",
};

export const Account = () => {
	const { client, status, persistence } = useOutletContext<AccountOutletContext>();
	const navigate = useNavigate();

	const [editing, setEditing] = useState(false);
	const [editValue, setEditValue] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	if (!client) return null;

	const accountId = client.accountId();
	const displayName = client.displayName?.() || accountId;
	const initials = displayName.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();

	const qrSvg = useMemo(
		() => encodeQR(`xmpp:${accountId}`, "svg"),
		[accountId],
	);

	const startEditing = () => {
		setEditValue(displayName);
		setEditing(true);
	};

	useEffect(() => {
		if (editing && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [editing]);

	const saveDisplayName = () => {
		const trimmed = editValue.trim();
		if (trimmed && trimmed !== displayName) {
			const c = client as any;
			if (typeof c.setDisplayName === "function") {
				c.setDisplayName(trimmed);
			} else if (typeof c.setNickname === "function") {
				c.setNickname(trimmed);
			}
		}
		setEditing(false);
	};

	const cancelEditing = () => {
		setEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			saveDisplayName();
		} else if (e.key === "Escape") {
			cancelEditing();
		}
	};

	const handleLogout = async () => {
		const confirmed = await confirmDialog(
			"Remove this account from the app? Your messages will be deleted from this device.",
		);
		if (!confirmed) return;
		(client as any).stop?.();
		const { accounts, setAccounts } = useAccountStore.getState();
		const { [accountId]: _, ...rest } = accounts;
		setAccounts(rest);
		(persistence as any)?.deleteAccount?.(accountId);
		navigate("/");
	};

	const dotColor = statusColor[status] ?? "bg-yellow-400";
	const label = statusLabel[status] ?? status;

	return (
		<div className="flex flex-col h-full p-6 max-w-lg mx-auto">
			<h1 className="text-xl font-semibold text-white mb-6">Account</h1>

			<div className="glass-panel rounded-2xl p-6 mb-4">
				<div className="flex items-center gap-4">
					<div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-xl font-medium">
						{initials}
					</div>
					<div className="flex-1 min-w-0">
						{editing ? (
							<div className="flex items-center gap-2">
								<input
									ref={inputRef}
									type="text"
									value={editValue}
									onChange={(e) => setEditValue(e.target.value)}
									onKeyDown={handleKeyDown}
									className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-white font-medium text-sm outline-none focus:border-primary w-full"
								/>
								<button
									onClick={saveDisplayName}
									className="text-green-400 hover:text-green-300 transition-colors shrink-0"
									title="Save"
								>
									<Check size={16} />
								</button>
								<button
									onClick={cancelEditing}
									className="text-white/50 hover:text-white/80 transition-colors shrink-0"
									title="Cancel"
								>
									<X size={16} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2">
								<p className="font-medium text-white truncate">{displayName}</p>
								<button
									onClick={startEditing}
									className="text-white/40 hover:text-white/80 transition-colors shrink-0"
									title="Edit display name"
								>
									<Pencil size={14} />
								</button>
							</div>
						)}
						<p className="text-sm text-white/55 truncate">{accountId}</p>
						<div className="flex items-center gap-1.5 mt-1">
							<div className={`w-2 h-2 rounded-full ${dotColor}`} />
							<span className="text-xs text-white/50">{label}</span>
						</div>
					</div>
				</div>
			</div>

			{/* QR code for sharing JID */}
			<div className="glass-panel rounded-2xl p-6 mb-4 flex flex-col items-center">
				<p className="text-sm text-white/60 mb-3">Share your address</p>
				<div
					className="bg-white p-4 rounded-2xl inline-block mx-auto"
					dangerouslySetInnerHTML={{ __html: qrSvg }}
				/>
				<p className="text-xs text-white/40 mt-3 font-mono">{accountId}</p>
			</div>

			<button
				onClick={handleLogout}
				className="flex items-center gap-3 px-4 py-3 rounded-xl glass-panel text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all text-sm"
			>
				<LogOut size={18} />
				Remove account
			</button>
		</div>
	);
};
